import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
    Checkbox,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useSelector } from 'react-redux';

const STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'new', label: 'New' },
    { value: 'open', label: 'Open' },
    { value: 'pending', label: 'Pending' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
];

const PRIORITY_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
];

const MAX_ATTACHMENTS = 10;

const statusChipColor = {
    new: 'default',
    open: 'warning',
    pending: 'info',
    resolved: 'success',
    closed: 'default',
};

const priorityChipColor = {
    low: 'success',
    medium: 'primary',
    high: 'warning',
    urgent: 'error',
};

const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const TicketListEmptyState = () => (
    <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed #cfd8dc' }}>
        <HelpOutlineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
            No support tickets yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
            Submit a ticket using the form above and it will appear here for quick access.
        </Typography>
    </Paper>
);

const ReplyBubble = ({ reply }) => {
    const isAgent = reply.authorType === 'agent';
    const isInternal = reply.internal;
    const bubbleBg = isInternal ? '#f3e5f5' : isAgent ? '#e3f2fd' : '#e8f5e9';
    const borderColor = isInternal ? '#ce93d8' : isAgent ? '#64b5f6' : '#81c784';
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 2,
                borderLeft: `4px solid ${borderColor}`,
                backgroundColor: bubbleBg,
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <Typography variant="subtitle2">{reply.authorName || 'Support User'}</Typography>
                <FiberManualRecordIcon sx={{ fontSize: 8, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                    {formatDateTime(reply.createdAt)}
                </Typography>
                {isInternal && (
                    <Chip size="small" label="Internal" color="secondary" variant="outlined" sx={{ ml: 1 }} />
                )}
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                {reply.message}
            </Typography>
            {Array.isArray(reply.attachments) && reply.attachments.length > 0 && (
                <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                    {reply.attachments.map((attachment) => (
                        <Chip
                            key={`${reply.id}-${attachment.url}`}
                            component="a"
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            label={attachment.name}
                            clickable
                            icon={<ChatBubbleOutlineIcon fontSize="small" />}
                            sx={{ maxWidth: 240 }}
                        />
                    ))}
                </Stack>
            )}
        </Paper>
    );
};

const SupportTicketWorkspace = ({ categories = [], initialTicketId, onTicketSelect }) => {
    const { token } = useSelector((state) => state.auth);

    const [tickets, setTickets] = useState([]);
    const [stats, setStats] = useState({ status: {}, priority: {}, total: 0 });
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [ticketsError, setTicketsError] = useState('');

    const [selectedTicketId, setSelectedTicketId] = useState(initialTicketId || '');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [thread, setThread] = useState([]);
    const [threadLoading, setThreadLoading] = useState(false);
    const [threadError, setThreadError] = useState('');

    const [filters, setFilters] = useState({ status: 'all', priority: 'all', search: '' });
    const [searchDraft, setSearchDraft] = useState('');

    const [formValues, setFormValues] = useState({
        subject: '',
        category: '',
        priority: 'medium',
        message: '',
        tags: '',
    });
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [formAttachments, setFormAttachments] = useState([]);
    const [formAttachmentUploading, setFormAttachmentUploading] = useState(false);
    const [formAttachmentError, setFormAttachmentError] = useState('');

    const [replyMessage, setReplyMessage] = useState('');
    const [markResolved, setMarkResolved] = useState(false);
    const [replySubmitting, setReplySubmitting] = useState(false);
    const [replyError, setReplyError] = useState('');
    const [replyAttachments, setReplyAttachments] = useState([]);
    const [replyAttachmentUploading, setReplyAttachmentUploading] = useState(false);
    const [replyAttachmentError, setReplyAttachmentError] = useState('');

    const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

    const categoryOptions = useMemo(() => {
        const base = categories.map((category) => ({ value: category.name, label: category.name }));
        const names = base.map((item) => item.value.toLowerCase());
        if (!names.includes('general')) {
            base.unshift({ value: 'General', label: 'General' });
        }
        return base;
    }, [categories]);

    const uploadAttachment = useCallback(
        async (file) => {
            if (!file) return null;
            const formData = new FormData();
            formData.append('file', file);
            const { data } = await axios.post('/api/support/tickets/attachments', formData, {
                headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
            });
            return data?.attachment || null;
        },
        [authHeaders]
    );

    const handleFormAttachmentChange = useCallback(
        async (event) => {
            const files = Array.from(event.target.files || []);
            event.target.value = '';
            if (!files.length) return;

            const availableSlots = MAX_ATTACHMENTS - formAttachments.length;
            if (availableSlots <= 0) {
                setFormAttachmentError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
                return;
            }

            let filesToUpload = files;
            if (files.length > availableSlots) {
                filesToUpload = files.slice(0, availableSlots);
                setFormAttachmentError(
                    `Only ${availableSlots} more attachment${availableSlots === 1 ? '' : 's'} can be added.`
                );
            } else {
                setFormAttachmentError('');
            }

            setFormAttachmentUploading(true);
            try {
                const uploaded = [];
                for (const file of filesToUpload) {
                    // eslint-disable-next-line no-await-in-loop
                    const attachment = await uploadAttachment(file);
                    if (attachment) {
                        uploaded.push(attachment);
                    }
                }
                if (uploaded.length) {
                    setFormAttachments((prev) => [...prev, ...uploaded]);
                }
            } catch (error) {
                const message = error.response?.data?.msg || error.message || 'Failed to upload attachment.';
                setFormAttachmentError(message);
            } finally {
                setFormAttachmentUploading(false);
            }
        },
        [formAttachments.length, uploadAttachment]
    );

    const handleRemoveFormAttachment = useCallback((url) => {
        setFormAttachments((prev) => prev.filter((attachment) => attachment.url !== url));
    }, []);

    const handleReplyAttachmentChange = useCallback(
        async (event) => {
            const files = Array.from(event.target.files || []);
            event.target.value = '';
            if (!files.length) return;

            const availableSlots = MAX_ATTACHMENTS - replyAttachments.length;
            if (availableSlots <= 0) {
                setReplyAttachmentError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
                return;
            }

            let filesToUpload = files;
            if (files.length > availableSlots) {
                filesToUpload = files.slice(0, availableSlots);
                setReplyAttachmentError(
                    `Only ${availableSlots} more attachment${availableSlots === 1 ? '' : 's'} can be added.`
                );
            } else {
                setReplyAttachmentError('');
            }

            setReplyAttachmentUploading(true);
            try {
                const uploaded = [];
                for (const file of filesToUpload) {
                    // eslint-disable-next-line no-await-in-loop
                    const attachment = await uploadAttachment(file);
                    if (attachment) {
                        uploaded.push(attachment);
                    }
                }
                if (uploaded.length) {
                    setReplyAttachments((prev) => [...prev, ...uploaded]);
                }
            } catch (error) {
                const message = error.response?.data?.msg || error.message || 'Failed to upload attachment.';
                setReplyAttachmentError(message);
            } finally {
                setReplyAttachmentUploading(false);
            }
        },
        [replyAttachments.length, uploadAttachment]
    );

    const handleRemoveReplyAttachment = useCallback((url) => {
        setReplyAttachments((prev) => prev.filter((attachment) => attachment.url !== url));
    }, []);

    const fetchStats = useCallback(async () => {
        if (!token) return;
        setStatsLoading(true);
        try {
            const { data } = await axios.get('/api/support/tickets/stats', { headers: authHeaders });
            setStats(data || { status: {}, priority: {}, total: 0 });
        } catch (error) {
            console.error('Failed to fetch ticket stats', error);
        } finally {
            setStatsLoading(false);
        }
    }, [token, authHeaders]);

    const fetchTickets = useCallback(async () => {
        if (!token) return;
        setTicketsLoading(true);
        setTicketsError('');
        try {
            const params = {};
            if (filters.status !== 'all') params.status = filters.status;
            if (filters.priority !== 'all') params.priority = filters.priority;
            if (filters.search) params.search = filters.search;
            const { data } = await axios.get('/api/support/tickets', { headers: authHeaders, params });
            setTickets(data?.tickets || []);
        } catch (error) {
            console.error('Failed to load support tickets', error);
            setTicketsError(error?.response?.data?.msg || 'Failed to load support tickets.');
        } finally {
            setTicketsLoading(false);
        }
    }, [token, authHeaders, filters.status, filters.priority, filters.search]);

    const fetchTicketDetails = useCallback(
        async (ticketId) => {
            if (!token || !ticketId) return;
            setThreadLoading(true);
            setThreadError('');
            try {
                const { data } = await axios.get(`/api/support/tickets/${ticketId}`, { headers: authHeaders });
                setSelectedTicket(data.ticket || null);
                setThread(data.replies || []);
            } catch (error) {
                console.error('Failed to load ticket details', error);
                setThreadError(error?.response?.data?.msg || 'Failed to load ticket details.');
            } finally {
                setThreadLoading(false);
            }
        },
        [token, authHeaders]
    );

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    useEffect(() => {
        setReplyAttachments([]);
        setReplyAttachmentError('');
        setReplyAttachmentUploading(false);
    }, [selectedTicketId]);

    useEffect(() => {
        if (initialTicketId && initialTicketId !== selectedTicketId) {
            setSelectedTicketId(initialTicketId);
        }
    }, [initialTicketId, selectedTicketId]);

    useEffect(() => {
        if (!selectedTicketId && tickets.length > 0) {
            const firstTicketId = tickets[0].id;
            setSelectedTicketId(firstTicketId);
            onTicketSelect?.(firstTicketId);
            return;
        }
        if (selectedTicketId && selectedTicketId !== 'new') {
            fetchTicketDetails(selectedTicketId);
        } else {
            setSelectedTicket(null);
            setThread([]);
            onTicketSelect?.(selectedTicketId === 'new' ? 'new' : '');
        }
    }, [selectedTicketId, tickets.length, fetchTicketDetails, onTicketSelect]);

    const handleApplySearch = () => {
        setFilters((prev) => ({ ...prev, search: searchDraft.trim() }));
    };

    const handleClearFilters = () => {
        setFilters({ status: 'all', priority: 'all', search: '' });
        setSearchDraft('');
    };

    const handleCreateTicket = async (event) => {
        event.preventDefault();
        if (!token) return;
        if (formAttachmentUploading) {
            setFormError('Please wait for attachments to finish uploading.');
            return;
        }
        setFormSubmitting(true);
        setFormError('');
        setFormSuccess('');
        try {
            const payload = {
                subject: formValues.subject.trim(),
                priority: formValues.priority,
                category: formValues.category || 'General',
                message: formValues.message.trim(),
            };
            if (!payload.subject) {
                throw new Error('Please provide a subject for your request.');
            }
            if (!payload.message) {
                throw new Error('Describe your issue in the message field.');
            }
            if (formValues.tags) {
                payload.tags = formValues.tags
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter(Boolean);
            }
            if (formAttachments.length) {
                payload.attachments = formAttachments;
            }
            const { data } = await axios.post('/api/support/tickets', payload, { headers: authHeaders });
            const createdTicket = data?.ticket;
            if (createdTicket) {
                setTickets((prev) => [createdTicket, ...prev]);
                setSelectedTicketId(createdTicket.id);
                setSelectedTicket(createdTicket);
                onTicketSelect?.(createdTicket.id);
                setThread(data?.reply ? [data.reply] : []);
                fetchStats();
                setFormSuccess('Support ticket submitted successfully. Our team will reach out soon.');
                setFormValues({ subject: '', category: '', priority: 'medium', message: '', tags: '' });
                setFormAttachments([]);
                setFormAttachmentError('');
            }
        } catch (error) {
            const message = error.response?.data?.msg || error.message || 'Failed to submit support ticket.';
            setFormError(message);
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleSendReply = async () => {
        if (!token || !selectedTicketId) return;
        if (!replyMessage.trim()) {
            setReplyError('Add a message before sending your reply.');
            return;
        }
        if (replyAttachmentUploading) {
            setReplyError('Please wait for attachments to finish uploading.');
            return;
        }
        setReplySubmitting(true);
        setReplyError('');
        try {
            const payload = { message: replyMessage.trim() };
            if (replyAttachments.length) {
                payload.attachments = replyAttachments;
            }
            if (markResolved) {
                payload.status = 'resolved';
            }
            const { data } = await axios.post(`/api/support/tickets/${selectedTicketId}/replies`, payload, {
                headers: authHeaders,
            });
            if (data?.reply) {
                setThread((prev) => [...prev, data.reply]);
            }
            if (data?.ticket) {
                setSelectedTicket(data.ticket);
                onTicketSelect?.(data.ticket.id);
                setTickets((prev) => prev.map((ticket) => (ticket.id === data.ticket.id ? data.ticket : ticket)));
                fetchStats();
            }
            setReplyMessage('');
            setMarkResolved(false);
            setReplyAttachments([]);
            setReplyAttachmentError('');
        } catch (error) {
            const message = error.response?.data?.msg || 'Failed to send reply.';
            setReplyError(message);
        } finally {
            setReplySubmitting(false);
        }
    };

    const handleRefreshTickets = () => {
        fetchTickets();
        fetchStats();
        if (selectedTicketId) {
            fetchTicketDetails(selectedTicketId);
        }
    };

    const activeStatusCount = (statusValue) => stats?.status?.[statusValue] || 0;

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={800} gutterBottom>My Tickets</Typography>
                <Typography variant="body1" color="text.secondary">View and manage your active support requests.</Typography>
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 2, 
                            borderRadius: 4, 
                            border: '1px solid #f0f0f0',
                            backgroundColor: '#fff',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2,
                            alignItems: { xs: 'stretch', md: 'center' }
                        }}
                    >
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                                <Chip
                                    key={option.value}
                                    label={`${option.label} ${activeStatusCount(option.value) > 0 ? `(${activeStatusCount(option.value)})` : ''}`}
                                    color={filters.status === option.value ? 'primary' : 'default'}
                                    variant={filters.status === option.value ? 'filled' : 'outlined'}
                                    onClick={() => setFilters((prev) => ({ ...prev, status: option.value }))}
                                    sx={{ borderRadius: 2, fontWeight: 600 }}
                                />
                            ))}
                        </Stack>
                        
                        <Box sx={{ flexGrow: 1 }} />

                        <Stack direction="row" spacing={1.5}>
                            <TextField
                                size="small"
                                select
                                label="Priority"
                                value={filters.priority}
                                onChange={(e) => setFilters((prev) => ({ ...prev, priority: e.target.value }))}
                                sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                            >
                                {PRIORITY_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                size="small"
                                placeholder="Search tickets..."
                                value={searchDraft}
                                onChange={(e) => setSearchDraft(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleApplySearch()}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" color="action" />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 2.5, backgroundColor: '#fcfcfc' }
                                }}
                                sx={{ minWidth: 200 }}
                            />
                            <Tooltip title="Refresh">
                                <IconButton 
                                    onClick={handleRefreshTickets} 
                                    sx={{ border: '1px solid #f0f0f0', borderRadius: 2.5 }}
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4} lg={3.5}>
                    <Stack spacing={4}>
                        {/* Ticket List */}
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                borderRadius: 4, 
                                border: '1px solid #f0f0f0', 
                                overflow: 'hidden',
                                backgroundColor: '#fff',
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: 600
                            }}
                        >
                            <Box sx={{ p: 2.5, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle1" fontWeight={700}>Conversations</Typography>
                                {ticketsLoading && <CircularProgress size={16} />}
                            </Box>
                            
                            <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                                {ticketsError && <Alert severity="error" sx={{ m: 2 }}>{ticketsError}</Alert>}
                                {!ticketsLoading && tickets.length === 0 ? (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">No tickets found.</Typography>
                                    </Box>
                                ) : (
                                    <List disablePadding>
                                        {tickets.map((ticket) => (
                                            <ListItemButton
                                                key={ticket.id}
                                                selected={ticket.id === selectedTicketId}
                                                onClick={() => {
                                                    setSelectedTicketId(ticket.id);
                                                    onTicketSelect?.(ticket.id);
                                                }}
                                                sx={{
                                                    py: 2,
                                                    px: 2.5,
                                                    borderLeft: '4px solid transparent',
                                                    '&.Mui-selected': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                        borderLeftColor: 'primary.main',
                                                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
                                                    }
                                                }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                            <Typography variant="subtitle2" noWrap fontWeight={selectedTicketId === ticket.id ? 700 : 600}>
                                                                {ticket.subject}
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: ticket.status === 'resolved' ? 'success.main' : 'warning.main',
                                                                    flexShrink: 0,
                                                                    ml: 1
                                                                }}
                                                            />
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                #{ticket.ticketNumber} • {ticket.category}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.disabled">
                                                                {formatDateTime(ticket.lastActivityAt)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        </Paper>

                        {/* Quick Actions / New Ticket Toggle */}
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => {
                                setSelectedTicketId('new');
                                onTicketSelect?.('new');
                            }}
                            sx={{ 
                                borderRadius: 3, 
                                py: 1.5, 
                                borderStyle: 'dashed',
                                textTransform: 'none',
                                fontWeight: 600
                            }}
                        >
                            Open a New Ticket
                        </Button>
                    </Stack>
                </Grid>

                <Grid item xs={12} md={8} lg={8.5}>
                    {!selectedTicketId || selectedTicketId === 'new' ? (
                        /* Create Ticket View */
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: { xs: 3, md: 5 }, 
                                borderRadius: 5, 
                                border: '1px solid #f0f0f0',
                                backgroundColor: '#fff'
                            }}
                        >
                            <Typography variant="h5" fontWeight={800} gutterBottom>Submit a Request</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                                Tell us more about what you need and we'll get back to you as soon as possible.
                            </Typography>

                            {formError && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{formError}</Alert>}
                            {formSuccess && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{formSuccess}</Alert>}

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        label="Subject"
                                        placeholder="Brief summary of your issue"
                                        value={formValues.subject}
                                        onChange={(e) => setFormValues(prev => ({ ...prev, subject: e.target.value }))}
                                        InputProps={{ sx: { borderRadius: 2.5 } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Category"
                                        value={formValues.category}
                                        onChange={(e) => setFormValues(prev => ({ ...prev, category: e.target.value }))}
                                        InputProps={{ sx: { borderRadius: 2.5 } }}
                                    >
                                        {categoryOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={6}
                                        label="How can we help?"
                                        placeholder="Please provide as much detail as possible..."
                                        value={formValues.message}
                                        onChange={(e) => setFormValues(prev => ({ ...prev, message: e.target.value }))}
                                        InputProps={{ sx: { borderRadius: 3 } }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ p: 2, border: '1px dashed #e0e0e0', borderRadius: 3, backgroundColor: '#fafafa' }}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Button
                                                variant="contained"
                                                component="label"
                                                size="small"
                                                startIcon={<AttachFileIcon />}
                                                disabled={formAttachmentUploading}
                                                sx={{ borderRadius: 2, boxShadow: 'none' }}
                                            >
                                                {formAttachmentUploading ? 'Uploading...' : 'Attach Files'}
                                                <input type="file" hidden multiple onChange={handleFormAttachmentChange} />
                                            </Button>
                                            <Typography variant="caption" color="text.secondary">
                                                Max 10 files, up to 20MB each.
                                            </Typography>
                                        </Stack>
                                        {formAttachments.length > 0 && (
                                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
                                                {formAttachments.map(att => (
                                                    <Chip
                                                        key={att.url}
                                                        label={att.name}
                                                        size="small"
                                                        onDelete={() => handleRemoveFormAttachment(att.url)}
                                                        sx={{ borderRadius: 1.5 }}
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        onClick={handleCreateTicket}
                                        disabled={formSubmitting || formAttachmentUploading}
                                        sx={{ borderRadius: 3, py: 1.5, fontWeight: 700, boxShadow: '0 8px 20px rgba(25, 118, 210, 0.2)' }}
                                    >
                                        {formSubmitting ? 'Submitting...' : 'Submit Support Request'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    ) : (
                        /* Conversation View */
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                borderRadius: 5, 
                                border: '1px solid #f0f0f0',
                                backgroundColor: '#fff',
                                display: 'flex',
                                flexDirection: 'column',
                                height: 700
                            }}
                        >
                            {/* Chat Header */}
                            <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" fontWeight={800}>{selectedTicket?.subject}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Ticket #{selectedTicket?.ticketNumber} • {selectedTicket?.category}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <Chip 
                                        label={selectedTicket?.status} 
                                        size="small" 
                                        color={statusChipColor[selectedTicket?.status] || 'default'}
                                        sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                                    />
                                    <IconButton onClick={() => fetchTicketDetails(selectedTicketId)}>
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Box>

                            {/* Chat Messages */}
                            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, backgroundColor: '#fcfcfc' }}>
                                {threadLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                                ) : (
                                    <Stack spacing={2}>
                                        {thread.map((reply) => (
                                            <Box 
                                                key={reply.id} 
                                                sx={{ 
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    alignItems: reply.authorType === 'agent' ? 'flex-start' : 'flex-end'
                                                }}
                                            >
                                                <Box 
                                                    sx={{ 
                                                        maxWidth: '85%',
                                                        p: 2, 
                                                        borderRadius: reply.authorType === 'agent' ? '0 16px 16px 16px' : '16px 0 16px 16px',
                                                        backgroundColor: reply.authorType === 'agent' ? '#fff' : '#e3f2fd',
                                                        color: 'text.primary',
                                                        boxShadow: reply.authorType === 'agent' ? '0 2px 10px rgba(0,0,0,0.03)' : '0 4px 15px rgba(25, 118, 210, 0.08)',
                                                        border: reply.authorType === 'agent' ? '1px solid #f0f0f0' : '1px solid #bbdefb'
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 700, opacity: 0.8 }}>
                                                        {reply.authorName}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {reply.message}
                                                    </Typography>
                                                    {reply.attachments?.length > 0 && (
                                                        <Stack spacing={0.5} mt={1.5}>
                                                            {reply.attachments.map(att => (
                                                                <Button 
                                                                    key={att.url}
                                                                    component="a"
                                                                    href={att.url}
                                                                    target="_blank"
                                                                    size="small"
                                                                    startIcon={<AttachFileIcon fontSize="small" />}
                                                                    sx={{ 
                                                                        color: 'primary.main',
                                                                        textTransform: 'none',
                                                                        justifyContent: 'flex-start',
                                                                        fontSize: '0.75rem',
                                                                        opacity: 0.9
                                                                    }}
                                                                >
                                                                    {att.name}
                                                                </Button>
                                                            ))}
                                                        </Stack>
                                                    )}
                                                </Box>
                                                <Typography variant="caption" sx={{ mt: 0.5, px: 1, color: 'text.disabled' }}>
                                                    {formatDateTime(reply.createdAt)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Box>

                            {/* Chat Input */}
                            <Box sx={{ p: 2.5, borderTop: '1px solid #f0f0f0' }}>
                                {replyError && <Alert severity="error" sx={{ mb: 2 }}>{replyError}</Alert>}
                                <Box sx={{ position: 'relative' }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        maxRows={4}
                                        placeholder="Type your message..."
                                        value={replyMessage}
                                        onChange={(e) => setReplyMessage(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendReply();
                                            }
                                        }}
                                        InputProps={{
                                            sx: { borderRadius: 4, pr: 12, backgroundColor: '#fafafa' }
                                        }}
                                    />
                                    <Box sx={{ position: 'absolute', right: 8, bottom: 8, display: 'flex', gap: 1 }}>
                                        <IconButton 
                                            component="label" 
                                            disabled={replyAttachmentUploading}
                                            size="small"
                                        >
                                            <AttachFileIcon fontSize="small" />
                                            <input type="file" hidden multiple onChange={handleReplyAttachmentChange} />
                                        </IconButton>
                                        <IconButton 
                                            color="primary" 
                                            onClick={handleSendReply}
                                            disabled={replySubmitting || !replyMessage.trim()}
                                            sx={{ backgroundColor: 'primary.main', color: '#fff', '&:hover': { backgroundColor: 'primary.dark' } }}
                                            size="small"
                                        >
                                            <SendIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                                {replyAttachments.length > 0 && (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" mt={1.5}>
                                        {replyAttachments.map(att => (
                                            <Chip
                                                key={att.url}
                                                label={att.name}
                                                size="small"
                                                onDelete={() => handleRemoveReplyAttachment(att.url)}
                                                sx={{ borderRadius: 1.5 }}
                                            />
                                        ))}
                                    </Stack>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox 
                                                size="small" 
                                                checked={markResolved} 
                                                onChange={(e) => setMarkResolved(e.target.checked)} 
                                            />
                                        }
                                        label={<Typography variant="caption">Mark as resolved</Typography>}
                                    />
                                </Box>
                            </Box>
                        </Paper>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default SupportTicketWorkspace;
